import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { PaymentMethod, PaymentMethodTemplate } from '../payment-method';
import { booleanConverter } from '../../../common/converters';
import { ClientTokenDetails, PaymentDetails, PaymentType, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { getAddressValidationError, getAddressValidationStatus } from '../../address/store/address-validation';
import { ActionStatus } from '../../../common/constants/action-status';
import { handleApiError } from '../../../common/utils/api-utils';
import {
  getActiveStepId,
  getClientTokenResponse,
  getPaymentDetails,
  updateBillingAddress,
  resetPaymentDetails,
  updatePaymentDetails,
  updateGooglePayBillingAddress,
  getGooglePayBillingAddress,
} from '../../checkout/shop-checkout';
import {
  isValidGooglePayDetail,
  render3rdPartyPaymentCardInfo,
} from '../../../common/utils/payment-util';
import emit from '../../../common/events';
import './google-pay-button';
import paymentStyles from '../../../common/styles/payment-styles';
import cardLogoStyles from '../../../common/styles/card-logo-styles';
import { MessageType } from '../../alert/global-message.interface';
import { getValidatedAddress } from '../../address/utils/address.util';
import { ERROR_ADDRESS_VALIDATION_MESSAGE } from '../utils/braintree.util';

/**
 * Google Pay payment method that loads Google Pay button.
 * Renders Google Pay payment results.
 */
@customElement(`mhecomm-google-pay-payment-method`)
export class GooglePayPaymentMethod extends connect(store)(PaymentMethod) implements PaymentMethodTemplate {
  static styles = [paymentStyles, cardLogoStyles];
  /**
   * Currency for Google Pay transaction.
   */
  @property()
  currency: string = 'USD';

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({ converter: booleanConverter })
  shippable: boolean = false;

  @state()
  private googlePaymentDetail: PaymentDetails | undefined;

  /**
   * Braintree Client token to initialize Google Pay button.
   */
  @state()
  private clientToken: string = '';

  /**
   * Controls the visibility of the error message when the address validation has failed
   */
  @state()
  private errorAddressValidation: boolean = false;

  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_GOOGLE_PAY, state);

    if (getAddressValidationStatus(state) === ActionStatus.failed) {
      handleApiError(getAddressValidationError(state));
      return;
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-google-pay-checkout-info-collected', this.onCollectGooglePayInfo);
    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(store.getState() as StoreState);
    this.clientToken = clientTokenResponse!.token;
    this.billingAddress = getGooglePayBillingAddress(store.getState() as StoreState)!;
    if (this.billingAddress) {
      store.dispatch(updateBillingAddress(this.billingAddress));
    }
    const paymentDetail = getPaymentDetails(store.getState() as StoreState);
    if (paymentDetail) {
      if (isValidGooglePayDetail(paymentDetail)) {
        this.googlePaymentDetail = paymentDetail;
      } else {
        store.dispatch(resetPaymentDetails());
      }
    }
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    if (this.googlePaymentDetail || this.errorAddressValidation) {
      return this.renderGooglePayPaymentData(this.googlePaymentDetail);
    } else {
      return this.renderGooglePayButton();
    }
  }

  renderGooglePayButton() {
    return html` <div class="d-flex justify-content-start">
      <div id="google-payment-method-button" data-automation-id="google-payment-method-button">
        <mhecomm-google-pay-button
          buttonColor="black"
          buttonType="pay"
          amount="${this.totalAmount}"
          currencyCode="${this.currency}"
          clientToken="${this.clientToken}"
          ?shippable=${this.shippable}
        >
        </mhecomm-google-pay-button>
      </div>
    </div>`;
  }

  renderGooglePayPaymentData(paymentDetails?: PaymentDetails) {
    return html`
      ${this.errorAddressValidation ? html`
        <div class="mt-3" id="gpay-invalid-address" data-automation-id="gpay-invalid-address">
          <mhecomm-alert type=${MessageType.danger} message=${ERROR_ADDRESS_VALIDATION_MESSAGE}></mhecomm-alert>
        </div>
      ` : nothing}
      <div id="payment-method-details-container" class="d-flex flex-row flex-wrap border rounded p-3">
        ${render3rdPartyPaymentCardInfo(this.billingAddress, paymentDetails)}
        <div class="d-inline col-12 col-sm-4 text-sm-end">
          <button
            class="btn btn-link p-0"
            @click=${this.removePaymentDetails}
            data-automation-id="gpay-payment-details-remove"
            aria-label="Remove Google Pay payment method"
            >Remove</button>
        </div>
      </div>
    `;
  }

  removePaymentDetails(): void {
    this.billingAddress = this.billingAddressInitialState;
    this.errorAddressValidation = false;
    this.googlePaymentDetail = undefined;
    store.dispatch(resetPaymentDetails("Google Pay"));
  }

  /**
   * Collects Google Pay billing address and validates it.
   * If it is valid, the Redux state is updated with the payment and billing address.
   * Otherwise, only the local state is updated to display the invalid data.
   *
   * @param e
   */
  public onCollectGooglePayInfo(e: Event): void {
    const event = e as CustomEvent;
    const googlePayBillingAddress: RmsBookstoreAddress = event.detail.billingAddress;

    getValidatedAddress(googlePayBillingAddress)
      .then((validatedAddress) => {
        store.dispatch(updateGooglePayBillingAddress(validatedAddress));
        store.dispatch(updateBillingAddress(validatedAddress));
        this.updateGooglePaymentDetails(event.detail.paymentDetails);
        this.errorAddressValidation = false;
      })
      .catch((error) => {
        this.billingAddress = googlePayBillingAddress;
        this.errorAddressValidation = true;
        this.googlePaymentDetail = event.detail.paymentDetails;
        console.error('failed to validate address', error);
      });
  }

  updateGooglePaymentDetails(paymentDetails: PaymentDetails) {
    store.dispatch(updatePaymentDetails(paymentDetails));
    const activeStepId = getActiveStepId(store.getState() as StoreState);
    emit({
      element: this,
      name: `${activeStepId}-checkout-step-complete`,
      detail: { id: activeStepId },
    });
  }

  public async validatePaymentMethod(): Promise<void> {
    const paymentDetails = getPaymentDetails(store.getState() as StoreState)!;
    if (!paymentDetails.paymentNonce) {
      throw new Error('Google Pay payment failed.');
    }
  }

  paymentMethodEditAction(): void {
    return;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-google-pay-payment-method': GooglePayPaymentMethod;
  }
}
